import { useEffect, useState } from "react";
import DashboardShell from "../components/DashboardShell";
import { api, formatApiError } from "../lib/api";
import { Megaphone, Users, ShieldOff, Trash2, KeyRound } from "lucide-react";

export default function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ first_name:"",last_name:"",email:"",phone:"",role:"admin",password:"" });
  const [broadcast, setBroadcast] = useState({ title:"", body:"", email_too:true });
  const [donations, setDonations] = useState({ items:[], total_paid_usd:0 });
  const [reports, setReports] = useState([]);
  const [msg, setMsg] = useState(""); const [err, setErr] = useState("");

  const load = async () => {
    const [u, d, r] = await Promise.all([
      api.get(`/admin/users`, { params:{ q }}),
      api.get(`/admin/donations`).catch(() => ({ data:{ items:[], total_paid_usd:0 }})),
      api.get(`/admin/reports`).catch(() => ({ data:{ items:[] }})),
    ]);
    setUsers(u.data.items || []); setDonations(d.data); setReports(r.data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const createUser = async () => {
    setErr(""); setMsg("");
    try { await api.post(`/admin/users`, form); setMsg("Admin created."); setForm({ first_name:"",last_name:"",email:"",phone:"",role:"admin",password:"" }); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const suspend = async (u) => { await api.patch(`/admin/users/${u.id}/suspend?suspend=true`); await load(); };
  const del = async (u) => { if (window.confirm(`Delete ${u.name}?`)) { await api.delete(`/admin/users/${u.id}`); await load(); }};
  const reset = async (u) => { const np = window.prompt(`New password for ${u.name} (12+ chars, upper, number, special):`); if (!np) return; try { await api.post(`/admin/users/reset-password`, { user_id: u.id, new_password: np }); setMsg("Password reset."); } catch (e) { setErr(formatApiError(e)); }};
  const sendBroadcast = async () => { try { const { data } = await api.post(`/admin/broadcast`, broadcast); setMsg(`Sent to ${data.sent} users.`); setBroadcast({ title:"",body:"",email_too:true }); } catch (e) { setErr(formatApiError(e)); }};

  return (
    <DashboardShell>
      <div className="space-y-6" data-testid="admin-panel-page">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#39ff14]">// Admin Console</div>
          <h1 className="text-2xl lg:text-3xl font-semibold mt-1">Platform Management</h1>
        </div>

        {(msg || err) && <div className={`csc-card p-3 text-xs ${err ? "text-red-400" : "text-[#39ff14]"}`}>{err || msg}</div>}

        <section className="csc-card p-5 space-y-3" data-testid="create-admin-section">
          <h2 className="font-semibold flex items-center gap-2"><Users size={14} className="text-[#00aaff]" /> Create admin / tech</h2>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="First name" value={form.first_name} onChange={(e)=>setForm({...form,first_name:e.target.value})} className="csc-input" data-testid="admin-first-name" />
            <input placeholder="Last name" value={form.last_name} onChange={(e)=>setForm({...form,last_name:e.target.value})} className="csc-input" data-testid="admin-last-name" />
            <input placeholder="Email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} className="csc-input" data-testid="admin-email" />
            <input placeholder="Phone" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} className="csc-input" data-testid="admin-phone" />
            <select value={form.role} onChange={(e)=>setForm({...form,role:e.target.value})} className="csc-input" data-testid="admin-role">
              <option value="admin">Admin</option><option value="tech">Tech Support</option><option value="super_admin">Super Admin</option>
            </select>
            <input type="password" placeholder="Initial password" value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})} className="csc-input" data-testid="admin-password" />
          </div>
          <button onClick={createUser} className="csc-btn-primary" data-testid="admin-create-btn">Create</button>
        </section>

        <section className="csc-card p-5" data-testid="users-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">All users</h2>
            <div className="flex gap-2">
              <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search…" className="csc-input !py-2" data-testid="admin-users-search" />
              <button onClick={load} className="csc-btn-ghost !py-2">Search</button>
            </div>
          </div>
          <div className="overflow-auto"><table className="w-full text-sm">
            <thead className="font-mono-tech text-[10px] uppercase tracking-wider text-[#94a3b8]"><tr><th className="text-left py-2">User</th><th>Role</th><th>Actions</th></tr></thead>
            <tbody>{users.map((u) => (
              <tr key={u.id} className="border-t border-[rgba(0,170,255,0.18)]"><td className="py-2">{u.name} <span className="font-mono-tech text-[10px] text-[#94a3b8]">@{u.handle} · {u.email}</span></td>
              <td className="py-2 text-[#00aaff] font-mono-tech text-[11px]">{u.role}</td>
              <td className="py-2 text-right space-x-1">
                <button onClick={()=>reset(u)} className="csc-btn-ghost !py-1.5 !px-2" title="Reset password" data-testid={`reset-${u.id}`}><KeyRound size={11} /></button>
                <button onClick={()=>suspend(u)} className="csc-btn-ghost !py-1.5 !px-2" title="Suspend" data-testid={`suspend-${u.id}`}><ShieldOff size={11} /></button>
                <button onClick={()=>del(u)} className="csc-btn-ghost !py-1.5 !px-2 hover:!border-red-400 hover:!text-red-400" data-testid={`delete-${u.id}`}><Trash2 size={11} /></button>
              </td></tr>))}</tbody>
          </table></div>
        </section>

        <section className="csc-card p-5 space-y-3" data-testid="broadcast-section">
          <h2 className="font-semibold flex items-center gap-2"><Megaphone size={14} className="text-[#39ff14]" /> Broadcast announcement</h2>
          <input placeholder="Title" value={broadcast.title} onChange={(e)=>setBroadcast({...broadcast,title:e.target.value})} className="csc-input" data-testid="broadcast-title" />
          <textarea placeholder="Body" rows={4} value={broadcast.body} onChange={(e)=>setBroadcast({...broadcast,body:e.target.value})} className="csc-input resize-none" data-testid="broadcast-body" />
          <label className="flex items-center gap-2 text-xs text-[#94a3b8]"><input type="checkbox" checked={broadcast.email_too} onChange={(e)=>setBroadcast({...broadcast,email_too:e.target.checked})} className="accent-[#39ff14]" /> Also send email</label>
          <button onClick={sendBroadcast} className="csc-btn-secondary" data-testid="broadcast-send">Send to everyone</button>
        </section>

        <section className="csc-card p-5" data-testid="donations-section">
          <h2 className="font-semibold mb-3">Donations · <span className="text-[#39ff14]">${donations.total_paid_usd.toFixed(2)}</span> total</h2>
          <table className="w-full text-sm">
            <thead className="font-mono-tech text-[10px] uppercase tracking-wider text-[#94a3b8]"><tr><th className="text-left py-2">Donor</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead>
            <tbody>{donations.items.map((d) => (
              <tr key={d.id} className="border-t border-[rgba(0,170,255,0.18)]"><td className="py-2">{d.donor_name}</td><td className="font-mono-tech">${d.amount_usd}</td><td className={d.status==="paid"?"text-[#39ff14]":"text-[#94a3b8]"}>{d.status}</td><td className="font-mono-tech text-[11px] text-[#94a3b8]">{new Date(d.created_at).toLocaleString()}</td></tr>
            ))}</tbody>
          </table>
        </section>

        <section className="csc-card p-5" data-testid="reports-section">
          <h2 className="font-semibold mb-3">Reports</h2>
          {reports.length === 0 ? <div className="text-xs text-[#94a3b8]">No reports.</div> : reports.map((r) => (
            <div key={r.id} className="py-2 border-t border-[rgba(0,170,255,0.18)] text-sm">
              <div className="text-[#94a3b8] font-mono-tech text-[11px]">post {r.post_id} · by {r.reporter_name}</div>
              <div>{r.reason}</div>
            </div>
          ))}
        </section>
      </div>
    </DashboardShell>
  );
}
